import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ApiError } from './lib/api.ts'
import { onUnauthenticated } from './lib/session.ts'
import { RouterProvider } from '@tanstack/react-router'
import { router } from './router.tsx'
import { AuthGate } from './auth/AuthGate.tsx'
import './styles/theme.css'

// A request refused as signed out (idle, revoked, expired) sends the whole
// app back to the sign-in screen rather than leaving a page of errors.
const signedOut = (error: unknown) => {
  if (error instanceof ApiError && error.status === 401) onUnauthenticated(queryClient)
}

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: signedOut }),
  mutationCache: new MutationCache({ onError: signedOut }),
  defaultOptions: {
    queries: {
      // Voter counts are expensive to compute and change only when a snapshot is
      // built, so refetching on every window focus would be pure load.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 1,
    },
  },
})

const root = document.getElementById('root')
if (!root) throw new Error('#root is missing from index.html')

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthGate>
        <RouterProvider router={router} />
      </AuthGate>
    </QueryClientProvider>
  </StrictMode>,
)
